import React from 'react';
import { PageHeader, InfoBlock, CTASmall } from '../components/PageBits';
import { ASSET, chapters, team, stats } from '../mock/data';
import { MapPin, Users, Heart, Sparkles, Shield } from 'lucide-react';

const values = [
  { icon: Heart, title: 'Compassion', desc: 'We listen with care and respond with dignity.' },
  { icon: Users, title: 'Unity', desc: 'We believe lasting change happens when people move together.' },
  { icon: Sparkles, title: 'Culture', desc: 'We honour heritage while welcoming diversity and belonging.' },
  { icon: Shield, title: 'Empowerment', desc: 'We create opportunities for individuals and communities to rise with confidence.' },
];

const About = () => (
  <>
    <PageHeader kicker="About REFOCC" title="A decade of" accent="compassion, unity, and purpose in motion." intro="REFOCC was born from a simple but powerful idea: communities become stronger when people come together with care, respect, and shared responsibility. What began as a collective spirit in Kerala continues to grow as a movement for charity, cultural development, and social empowerment." />

    <section className="pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <img src={`${ASSET}/group-photo.jpg`} alt="REFOCC team" className="w-full rounded-[2rem] aspect-[16/8] object-cover" />
      </div>
    </section>

    <InfoBlock title="Our story" img={`${ASSET}/summit-01.jpg`}>
      <p>REFOCC is a vibrant tapestry woven by individuals from many backgrounds, cultures, and life experiences. Each person brings a unique strength to the organization, but the common goal remains the same — to build a world where compassion, unity, and empowerment flourish.</p>
      <p>The strength of REFOCC lies in collaboration. Its members believe that positive change is most meaningful when it is shaped with the community, not simply delivered to it. Through welfare initiatives, cultural programmes, women's empowerment, youth participation, and educational support, REFOCC continues to turn shared concern into shared action.</p>
      <div className="pt-4"><CTASmall label="Join the movement" /></div>
    </InfoBlock>

    <section className="py-16 bg-refocc-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-4xl text-refocc-dark text-center mb-3">Our values</h2>
        <p className="text-center text-refocc-dark/70 max-w-xl mx-auto mb-12">Four words. A whole worldview.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map(v => (
            <div key={v.title} className="bg-white rounded-2xl p-6 border border-refocc-dark/10">
              <div className="w-12 h-12 rounded-full bg-refocc-orange/15 text-refocc-orange flex items-center justify-center mb-4"><v.icon size={20}/></div>
              <h3 className="font-serif text-xl text-refocc-dark">{v.title}</h3>
              <p className="text-sm text-refocc-dark/65 mt-2 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-refocc-cream">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-4xl text-refocc-dark text-center mb-3">Community Identity</h2>
        <p className="text-center text-refocc-dark/70 max-w-3xl mx-auto mb-8 leading-relaxed">Within REFOCC, people are not only contributors; they are co-creators of a kinder, more connected society. The organization stands at the meeting point of stories, cultures, and shared hopes, shaping a future where kindness has no boundaries.</p>
      </div>
    </section>

    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-6 text-center">
          {stats.map(s => (
            <div key={s.label} className="p-8 rounded-3xl bg-white border border-refocc-dark/10">
              <div className="font-serif text-5xl text-refocc-orange">{s.num}</div>
              <div className="text-sm text-refocc-dark/65 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-refocc-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-4xl text-refocc-dark text-center mb-3">Leadership</h2>
        <p className="text-center text-refocc-dark/70 max-w-xl mx-auto mb-12">The hands and hearts shaping REFOCC's everyday.</p>

        {/* Top leadership: President, Secretary, Treasurer */}
        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {team.filter(t => ['President', 'Secretary', 'Treasurer'].includes(t.role)).map(t => (
            <div key={t.name} className="bg-white rounded-3xl overflow-hidden border border-refocc-dark/10">
              <div className="aspect-square overflow-hidden"><img src={t.img} alt={t.name} className="w-full h-full object-cover img-hover" /></div>
              <div className="p-5 text-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-refocc-orange mb-1">{t.role}</p>
                <h3 className="font-serif text-xl text-refocc-dark">{t.name}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* Vice Presidents & Joint Secretaries */}
        <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {/* Vice Presidents */}
          <div>
            <h3 className="font-serif text-2xl text-refocc-dark text-center mb-6">Vice Presidents</h3>
            <div className="grid grid-cols-2 gap-5">
              {team.filter(t => t.role === 'Vice President').map(t => (
                <div key={t.name} className="bg-white rounded-3xl overflow-hidden border border-refocc-dark/10">
                  <div className="aspect-square overflow-hidden"><img src={t.img} alt={t.name} className="w-full h-full object-cover img-hover" /></div>
                  <div className="p-4 text-center">
                    <h3 className="font-serif text-lg text-refocc-dark">{t.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Joint Secretaries */}
          <div>
            <h3 className="font-serif text-2xl text-refocc-dark text-center mb-6">Joint Secretaries</h3>
            <div className="grid grid-cols-2 gap-5">
              {team.filter(t => t.role === 'Joint Secretary').map(t => (
                <div key={t.name} className="bg-white rounded-3xl overflow-hidden border border-refocc-dark/10">
                  <div className="aspect-square overflow-hidden"><img src={t.img} alt={t.name} className="w-full h-full object-cover img-hover" /></div>
                  <div className="p-4 text-center">
                    <h3 className="font-serif text-lg text-refocc-dark">{t.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-4xl text-refocc-dark text-center mb-3">Chapters</h2>
        <p className="text-center text-refocc-dark/70 max-w-xl mx-auto mb-12">Seven international chapters and growing. Each rooted in its city, connected through purpose.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {chapters.map(c => (
            <div key={c.name} className="bg-white border border-refocc-dark/10 rounded-2xl p-5 hover:border-refocc-orange transition-colors">
              <div className="flex items-center gap-2 text-refocc-orange text-xs uppercase tracking-wider"><MapPin size={12}/> {c.country}</div>
              <h3 className="font-serif text-xl text-refocc-dark mt-2">{c.name}</h3>
              <p className="text-sm text-refocc-dark/60">{c.city}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default About;
